// src/api/messaging/typingApi.ts
import { ENV } from '../../config/runtimeEnv';
import CommunicationsService from '../../services/CommunicationsService';
import { TypingRequest, TypingResponse } from '../../types/messaging';
import type { UserUuid } from '../../types/identity';
import { createServiceClient } from '../../lib/http/client';

export const API_BASE_URL = ENV.VITE_NOTIFICATION_BASE_URL;

const createApiInstance = () => {
	const socketId = CommunicationsService.getSocketId();
	const headers: Record<string, string> = {
		'Content-Type': 'application/json',
	};
	if (socketId) {
		headers['X-Socket-ID'] = socketId;
	}
	return createServiceClient({ baseURL: API_BASE_URL, headers });
};

/**
 * Start typing indicator
 */
export const startTyping = async (conversationId: string, request: TypingRequest): Promise<void> => {
	try {
		const api = createApiInstance();
		await api.post(`/conversations/${conversationId}/typing/start`, request);
	} catch (error) {
		console.error('Error starting typing indicator:', error);
		throw error;
	}
};

/**
 * Stop typing indicator
 */
export const stopTyping = async (conversationId: string, request: TypingRequest): Promise<void> => {
	try {
		const api = createApiInstance();
		await api.post(`/conversations/${conversationId}/typing/stop`, request);
	} catch (error) {
		console.error('Error stopping typing indicator:', error);
		throw error;
	}
};

/**
 * Get active typers in a conversation
 */
export const getActiveTypers = async (conversationId: string, userId: UserUuid): Promise<UserUuid[]> => {
	try {
		const api = createApiInstance();
		const response = await api.get<TypingResponse>(`/conversations/${conversationId}/typing/active`, {
			params: { user_id: userId },
		});
		return response.data.data;
	} catch (error) {
		console.error('Error fetching active typers:', error);
		throw error;
	}
};

/**
 * Helper: Debounced typing indicator
 * Use this to avoid sending too many typing requests
 */
export class TypingIndicatorManager {
	private typingTimer: ReturnType<typeof setTimeout> | null = null;
	private isTyping: boolean = false;
	private conversationId: string;
	private userId: UserUuid;

	constructor(conversationId: string, userId: UserUuid) {
		this.conversationId = conversationId;
		this.userId = userId;
	}

	/**
	 * Call this when user types
	 */
	onTyping(): void {
		if (!this.isTyping) {
			this.isTyping = true;
			startTyping(this.conversationId, { user_id: this.userId }).catch((error) => {
				console.error('Failed to start typing:', error);
			});
		}

		// Reset the timer
		if (this.typingTimer) {
			clearTimeout(this.typingTimer);
		}

		// Stop typing after 3 seconds of inactivity
		this.typingTimer = setTimeout(() => {
			this.stop();
		}, 3000);
	}

	/**
	 * Call this when user stops typing or sends message
	 */
	stop(): void {
		if (this.typingTimer) {
			clearTimeout(this.typingTimer);
			this.typingTimer = null;
		}

		if (this.isTyping) {
			this.isTyping = false;
			stopTyping(this.conversationId, { user_id: this.userId }).catch((error) => {
				console.error('Failed to stop typing:', error);
			});
		}
	}

	/**
	 * Cleanup
	 */
	destroy(): void {
		this.stop();
	}
}